import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Activity, ChevronDown, ChevronUp, CircleCheck, CircleX, DollarSign, DraftingCompass, FileText, FolderOpen, Layers } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import { useBasisOfDesign } from "@/budget/components/dashboard/useBasisOfDesign";
import { BodBoard } from "@/budget/components/dashboard/BodBoard";
import { OpenBudgetsTable } from "@/budget/components/dashboard/OpenBudgetsTable";
import { ApprovedEqualDialog } from "@/budget/components/dashboard/ApprovedEqualDialog";

const API = `${window.location.origin}/api/budget`;

const PILL = {
  "Budget created": "bg-slate-100 text-slate-700", "Price overridden": "bg-amber-50 text-amber-800",
  "Budget updated": "bg-slate-50 text-slate-600", "Budget deleted": "bg-red-50 text-red-600",
  "User created": "bg-green-50 text-green-700", "Pricing updated": "bg-amber-50 text-amber-700",
  "Scope updated": "bg-purple-50 text-purple-700", "Database seeded": "bg-slate-50 text-slate-500",
  "Email sent": "bg-emerald-50 text-emerald-700",
};
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const when = (t) => (t ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const Card = ({ title, icon: Icon, children, testId, right }) => (
  <div className="bg-card text-card-foreground border border-border rounded-sm mb-8" data-testid={testId}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
      {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      <div className="ml-auto flex items-center gap-2">{right}</div>
    </div>
    {children}
  </div>
);

const StatBand = ({ stats }) => {
  const cards = [
    ["Total Budgets", stats.total_budgets, FileText, "", "total"],
    ["Revenue", money(stats.revenue), DollarSign, "", "revenue"],
    ["BOD", stats.basis_of_design, CircleCheck, "text-emerald-600", "bod"],
    ["Approved Equal", stats.approved_equal, CircleCheck, "text-teal-600", "approved-equal"],
    ["Not Listed", stats.not_listed, CircleX, "text-red-600", "not-listed"],
    ["Active Scopes", `${stats.active_scopes}/${stats.total_scopes}`, Layers, "", "scopes"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="bb-stat-band">
      {cards.map(([label, value, Icon, tone, key]) => (
        <div key={key} className="bg-card text-card-foreground border border-border rounded-sm p-4" data-testid={`admin-stat-${key}`}>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-medium ${tone || "text-muted-foreground"}`}>{label}</span>
            <Icon className={`w-4 h-4 ${tone || "text-muted-foreground"}`} />
          </div>
          <div className={`text-2xl font-semibold mt-1 ${tone || "text-foreground"}`} data-testid={`admin-stat-${key}-value`}>{value}</div>
        </div>
      ))}
    </div>
  );
};

/** BB-DASH-1 — SPEC TRACKER, then OPEN BUDGETS (one table), then RECENT ACTIVITY. */
/** BOD-1 — BASIS OF DESIGN, then OPEN BUDGETS (one table), then RECENT ACTIVITY. */
export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState([]);
  const bod = useBasisOfDesign();
  const [activity, setActivity] = useState([]);
  const [openSearch, setOpenSearch] = useState("");
  const openQ = openSearch.trim().toLowerCase();
  const [bodSearch, setBodSearch] = useState("");
  const bodQ = bodSearch.trim().toLowerCase();
  const bodHit = (r) => !bodQ || [r.project_name, r.customer, r.gc_name, r.architect, r.scope_code, r.scope_name, r.scope_alias, r.account_manager,
    r.gc_contact_name, r.gc_contact_email, r.architect_contact_name, r.architect_contact_email, (r.manufacturers || []).join(" ")]
    .join(" ").toLowerCase().includes(bodQ);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/activity`, { params: { limit: 200 } }).then((r) => setActivity(r.data || [])).catch(() => {}); }, []);

  const loadStats = async () => {
    try {
      const [s, ob] = await Promise.all([axios.get(`${API}/admin/stats`), axios.get(`${API}/admin/open-budgets/table`)]);
      setStats(s.data); setOpen(ob.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the dashboard"); }
  };
  useEffect(() => { loadStats(); }, []);

  const patchSpec = async (row, body) => { if (await bod.patch(row, body) && body.spec_status) loadStats(); };
  const closeEmail = (sent) => { bod.closeEmail(sent); if (sent) loadStats(); };

  return (
    <div className="p-4" data-testid="bb-admin-dashboard">
      {stats && <StatBand stats={stats} />}

      <Card title="Open Budgets" icon={FolderOpen} testId="open-budgets"
            right={<SearchBar value={openSearch} onChange={setOpenSearch} placeholder="Search..." testId="open-budgets-search" />}>
        <OpenBudgetsTable rows={open.filter((r) => !openQ || [r.project_name, r.customer, r.gc_name, r.type, r.status].some((v) => String(v || "").toLowerCase().includes(openQ)))}
                          openArchive={!!openQ} onOpen={(r) => nav(`/budgets/${r.budget_id}`)} />
      </Card>

      <Card title="Basis of Design" icon={DraftingCompass} testId="spec-tracker"
            right={<SearchBar value={bodSearch} onChange={setBodSearch} placeholder="Search..." testId="bod-dashboard-search" />}>
        <BodBoard bod={{ ...bod, rows: bod.rows.filter(bodHit) }} onPatch={patchSpec} toolbar={false} openArchive={!!bodQ}
                  emptyText={bodQ ? "Nothing matches — check the Archive below." : "No scope is marked for Basis of Design yet — turn one on in Manufacturers."} />
      </Card>

      <Card title="Activity" icon={Activity} testId="recent-activity"
            right={activity.length > 7 && (
              <button onClick={() => setShowAll((v) => !v)} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="activity-toggle-all">
                {showAll ? <><ChevronUp className="h-3.5 w-3.5" /> Show last 7</> : <><ChevronDown className="h-3.5 w-3.5" /> Show all · {activity.length}</>}
              </button>)}>
        {activity.length === 0 ? <div className="p-4 text-sm text-muted-foreground">Nothing logged yet.</div> : (
          <ul className={showAll ? "max-h-[32rem] overflow-y-auto" : ""} data-testid="activity-list" data-expanded={showAll ? "1" : "0"}>
            {(showAll ? activity : activity.slice(0, 7)).map((a, i) => (
              <li key={a.id || i} className="flex items-center gap-2 px-4 py-2 border-t border-border text-sm" data-testid={`activity-item-${i}`}>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${PILL[a.action] || "bg-slate-50 text-slate-600"}`}>{a.action}</span>
                <span className="truncate flex-1">{a.entity_name || a.entity_type}</span>
                <span className="text-xs text-slate-500">{a.user_name}</span>
                <span className="text-xs text-slate-400 w-32 text-right">{when(a.timestamp)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ApprovedEqualDialog row={bod.emailRow} onClose={closeEmail} />
    </div>
  );
}
